import { array, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { next } from '@ember/runloop';
import { cached } from '@glimmer/tracking';

import themed from 'webapp/decorators/themed';
import eq from 'webapp/helpers/eq';
import joinClasses from 'webapp/helpers/join-classes';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormRadiobuttonComponent from 'webapp/components/form/radiobutton';
import type { Args as RadiobuttonGroupComponentArgs } from 'webapp/components/form/radiobutton-group';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import ToolTipComponent from 'webapp/components/tool-tip';

export interface BoxItemInfo {
	value: AnySimpleValue;
	label: string;
	label2?: string;
	isDisabled?: boolean;
	showAddon?: boolean;
	badge?: string;
	tooltip?: string;
	icon?: IconName;
	label2Icon?: IconName;
}

interface Args extends RadiobuttonGroupComponentArgs {
	options: BoxItemInfo[];
	isDisabled?: boolean;
	extraClass?: string;
}

@themed()
export default class FormRadiobuttonBoxGroupComponent extends FormRadiobuttonGroupComponent<Args> {
	@cached
	override get options() {
		return this.args.options.map((option) => {
			const isDisabled = this.isOptionDisabled(option);
			return Object.assign(
				{},
				option,
				{
					disabled: isDisabled,
				},
				option,
			);
		});
	}

	@action
	onChange(value: AnySimpleValue) {
		const option = this.options.find((option) => option.value === value);
		if (!option || option.disabled) {
			return;
		}

		// in onOutsideClick method, target.isConnected is false, so we have to triger the change in the next run loop to delay the rerendering
		next(() => {
			this.args.onChange(value);
		});
	}

	private isOptionDisabled(option: BoxItemInfo) {
		const isSelected = option.value === this.args.selected;
		return option.isDisabled || (this.args.isDisabled && !isSelected);
	}
	<template>
		<ul class={{joinClasses (array "radiobutton-box-group" @extraClass)}}>
			{{#each this.options as |item|}}
				<li
					class={{joinClasses
						(array
							"radiobutton-box-group__item"
							(if (eq @selected item.value) "radiobutton-box-group__item--selected")
							(if item.disabled "radiobutton-box-group__item--disabled")
						)
					}}
				>
					<div role="button" {{on "click" (fn this.onChange item.value)}}>
						<div class="wrapper">
							<div class="radiobutton-default">
								<FormRadiobuttonComponent
									@name={{this.uniqueName}}
									@selected={{@selected}}
									@value={{item.value}}
									@label=""
									@isDisabled={{item.disabled}}
									@theme={{this.theme}}
									@size={{this.size}}
									@onChange={{@onChange}}
								/>
							</div>
							<div class="labels-wrapper">
								<span class="label1">
									{{item.label}}
									{{#if item.showAddon}}
										<div class="addon-tag">Add-on</div>
									{{/if}}
									{{#if item.badge}}
										<span class="badge2 badge2--outline">{{item.badge}}</span>
									{{/if}}
								</span>
								<span class="label2">
									{{item.label2}}
									{{#if item.label2Icon}}
										<CommonUiIconComponent
											@name={{item.label2Icon}}
											@size="sm"
										/>
									{{/if}}
								</span>
							</div>
						</div>
						{{#if item.icon}}
							<CommonUiIconComponent @name={{item.icon}} @size="lg" />
						{{/if}}
					</div>
					{{#if item.tooltip}}
						<ToolTipComponent
							@text={{item.tooltip}}
							@position="top"
							@interactive={{true}}
						/>
					{{/if}}
				</li>
			{{/each}}
		</ul>
	</template>
}
